import React, { Component } from 'react';
import {
  Grid,
  Row,
  Col,
  ButtonToolbar,
  Button,
  ButtonGroup,
  FormGroup,
  Radio,
  Panel,
  Glyphicon
} from 'react-bootstrap';
import { DebounceInput } from 'react-debounce-input';

import { clean_minutes_markup } from './clean_minutes_markup.js';
import {
  render_minutes_markup,
  newline_escape_remove
} from './render_minutes_markup.js';

import update from 'immutability-helper';
import dateFormat from 'dateformat';

import './MinutesEditor.css';

import ButtonSequence from './ButtonSequence.js';

import { test_cases } from './test_cases.js';

import FaPlusSquare from 'react-icons/lib/fa/plus-square';
import FaRotateLeft from 'react-icons/lib/fa/rotate-left';
import FaTrash from 'react-icons/lib/fa/trash';
import FaCheck from 'react-icons/lib/fa/check';
import FaDiamond from 'react-icons/lib/fa/diamond';

import keyboardJS from 'keyboardjs';

import {
  guess_denson,
  guess_denson_year,
  analyze_denson_song_references
} from './denson_song_references.js';

const empty_preview =
  '<div class="minutes_event empty" showplaceholder=true data-placeholder="Published view will appear here..."/>'; // see css.

class MinutesEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      //// Document Data

      source_text: '',
      denson_status: null, // null, 'denson', 'mixed', or 'non-denson'
      denson_year: null, // null, '2025', '1991', or 'N/A'

      prior_versions: [],
      prior_activated: false, // Whether an available prior version has been activated since last save, and/or the last saved version is top of stack.
      prior_number: -1, // The item number of the activated version (only meaningful when prior_activated = true).

      //// Global-UI State

      test_cases_expanded: false,

      //// Derived / compiled from Document data

      // ... Cleaning analysis results

      needs_cleaning: false,

      // ... Denson status guessing results

      guessed_denson_status: null, // null, 'denson', 'mixed', or 'non-denson'
      guessed_denson_year: null, // null, '2025', '1991', or 'N/A'

      // ... song usage analysis results

      source_text_with_song_markups: null, // will be null if no song markup analysis attempted (denson_status null or 'non-denson')
      song_markups_known_good: [], // songs that matched denson list
      song_markups_corrected: [], // songs corrected to match denson list: pairs: [bad => good]
      song_markups_ambiguous: [], // songs without t or b that have both t and b in Denson
      song_markups_bad: [], // apparent song numbers not present in Denson at all

      song_markups_known_good_count: 0, // counts of above lists
      song_markups_corrected_count: 0,
      song_markups_ambiguous_count: 0,
      song_markups_bad_count: 0,

      song_markups_problem_count: 0,
      song_markups_total_count: 0,
      song_markups_problem_percent: 0, // percentage expressed as integer from 0 - 100

      // ... rendering results and metadata

      // main result is the rendered html.  Note: generating HTML from user input is dangerous.
      rendered_html: empty_preview,

      // ... and a useful side effect is parsed metadata
      name_line: null,
      location_line: null,
      date_line: null,
      start_date: null,
      end_date: null,
      day_count: 0,
      date_range_display: null
    };

    // Updates to Document Data
    this.setSourceText = this.setSourceText.bind(this);
    this.setSourceTextEvent = this.setSourceTextEvent.bind(this);
    this.setDensonStatus = this.setDensonStatus.bind(this);
    this.setDensonStatusEvent = this.setDensonStatusEvent.bind(this);
    this.setDensonYear = this.setDensonYear.bind(this);
    this.setDensonYearEvent = this.setDensonYearEvent.bind(this);
    this.clearPriorVersions = this.clearPriorVersions.bind(this);
    this.saveAVersion = this.saveAVersion.bind(this);
    this.needToSave = this.needToSave.bind(this);
    this.saveAVersionIfNeeded = this.saveAVersionIfNeeded.bind(this);
    this.revertToVersionEvent = this.revertToVersionEvent.bind(this);
    this.resetDocumentData = this.resetDocumentData.bind(this);
    this.resetDocumentDataEvent = this.resetDocumentDataEvent.bind(this);
    this.setTestCaseEvent = this.setTestCaseEvent.bind(this);

    // Global UI State
    this.setTestCases = this.setTestCases.bind(this);
    this.toggleTestCasesEvent = this.toggleTestCasesEvent.bind(this);
    this.resetTestCasesState = this.resetTestCasesState.bind(this);

    // Document Data Modifying Actions
    this.cleanMeEvent = this.cleanMeEvent.bind(this);
    this.saveOnFirstPaste = this.saveOnFirstPaste.bind(this);

    // Helper functions used by Render to help component UI State reflect Document State
    this.ableToResetDocumentData = this.ableToResetDocumentData.bind(this);
    this.textMatchesVersion = this.textMatchesVersion.bind(this);
    this.versionButtonStyle = this.versionButtonStyle.bind(this);

    // Data derived entirely from Document State
    this.reCalculateDerivedData = this.reCalculateDerivedData.bind(this);
    this.componentDidMount = this.componentDidMount.bind(this);
  }

  componentDidMount() {
    let editor = this;
    keyboardJS.bind(['command + shift + t', 'ctrl + shift + t'], function(e) {
      editor.toggleTestCasesEvent();
      e.stopPropagation();
      e.preventDefault();
      return false; // Should have same effect as above, but seems to be needed anyway.
    });
  }

  componentWillUnmount() {
    keyboardJS.unbind('command + shift + t', () => {});
  }

  // Updates to Document Data
  // Note that calls to setState must use the chainable variant.

  setSourceText(new_text) {
    this.setState((previousState, currentProps) =>
      update(previousState, {
        source_text: { $set: new_text }
      })
    );
  }

  setSourceTextEvent(new_text) {
    this.setSourceText(new_text);
    this.reCalculateDerivedData(
      new_text,
      this.state.denson_status,
      this.state.denson_year
    );
  }

  setDensonStatus(new_denson_status) {
    this.setState((previousState, currentProps) =>
      update(previousState, {
        denson_status: { $set: new_denson_status }
      })
    );
  }

  setDensonStatusEvent(new_denson_status) {
    this.setDensonStatus(new_denson_status);
    this.reCalculateDerivedData(
      this.state.source_text,
      new_denson_status,
      this.state.denson_year
    );
  }

  setDensonYear(new_denson_year) {
    this.setState((previousState, currentProps) =>
      update(previousState, {
        denson_year: { $set: new_denson_year }
      })
    );
  }

  setDensonYearEvent(new_denson_year) {
    this.setDensonYear(new_denson_year);
    this.reCalculateDerivedData(
      this.state.source_text,
      this.state.denson_status,
      new_denson_year
    );
  }

  clearPriorVersions() {
    this.setState((previousState, currentProps) =>
      update(previousState, {
        prior_versions: { $set: [] },
        prior_activated: { $set: false },
        prior_number: { $set: -1 }
      })
    );
  }

  saveAVersion(new_text) {
    this.setState((previousState, currentProps) =>
      update(previousState, {
        prior_versions: { $push: [new_text] },
        prior_activated: { $set: true },
        // +1 to reflect the push, -1 because the number is zero-based
        prior_number: { $set: previousState.prior_versions.length + 1 - 1 }
      })
    );
  }

  needToSave(maybe_new_text) {
    if (!(typeof maybe_new_text === 'string')) {
      return false; // not a string -- don't save.
    }
    if (!(maybe_new_text.length > 0)) {
      return false; // empty string -- don't save.
    }
    if (this.state.prior_versions.length < 1) {
      return true; // no prior history -- do save!
    }

    // We have to determine which text we're comparing to.
    // If a prior version is activated, we're comparing to that one,
    // otherwise, to top of history.

    if (this.state.prior_activated) {
      let current_text_index = this.state.prior_number;
      let current_text = this.state.prior_versions[current_text_index];
      if (current_text === maybe_new_text) {
        return false; // same as activated version ... don't save!
      }
    } else {
      // Otherwise, we're comparing to top of history.
      let current_text_index = this.state.prior_versions.length - 1;
      let current_text = this.state.prior_versions[current_text_index];
      if (current_text === maybe_new_text) {
        return false; // text same as top of stack ... don't save!
      }
    }

    return true; // text must be different from top of stack... save!
  }

  saveAVersionIfNeeded(maybe_new_text) {
    // Nothing to do if the proposed text exactly matches the top of stack prior version.
    if (!this.needToSave(maybe_new_text)) {
      return;
    }
    this.saveAVersion(maybe_new_text);
  }

  revertToVersionEvent(i) {
    // Before we replace it, save the text currently being edited, if the user forgot to.
    this.saveAVersionIfNeeded(this.state.source_text);

    let new_text = this.state.prior_versions[i];
    this.setSourceText(new_text);

    this.setState((previousState, currentProps) =>
      update(previousState, {
        prior_activated: { $set: true },
        prior_number: { $set: i }
      })
    );
    this.reCalculateDerivedData(
      new_text,
      this.state.denson_status,
      this.state.denson_year
    );
  }

  resetDocumentData() {
    this.setSourceText('');
    this.setDensonStatus(null);
    this.clearPriorVersions();
  }

  resetDocumentDataEvent() {
    // aka trash
    this.resetDocumentData();
    this.reCalculateDerivedData('', null, null);
  }

  setTestCaseEvent(i) {
    this.resetDocumentData();
    this.setSourceText(test_cases[i]);
    this.saveAVersion(test_cases[i]);
    this.reCalculateDerivedData(test_cases[i], null, null);
  }

  // Global UI State -- test cases

  setTestCases(new_expanded_state) {
    this.setState((previousState, currentProps) =>
      update(previousState, {
        test_cases_expanded: { $set: new_expanded_state }
      })
    );
  }

  resetTestCasesState() {
    this.setTestCases(false);
  }

  toggleTestCasesEvent() {
    this.setState((previousState, currentProps) =>
      update(previousState, {
        test_cases_expanded: { $set: !previousState.test_cases_expanded }
      })
    );
  }

  // Document Data Modifying Actions

  cleanMeEvent(text) {
    let old_text = this.state.source_text;

    // Before we replace it, save the old text if the user forgot to.
    this.saveAVersionIfNeeded(old_text);

    let new_text = clean_minutes_markup(old_text);
    this.setSourceText(new_text);

    // If there were changes, also save the new, cleaned text so the user can come back here.
    // Note we can't call saveAVersionIfNeeded because needToSave looks at this.state which won't have been updated yet until the next render.
    if (old_text !== new_text) {
      this.saveAVersion(new_text);
    }
    this.reCalculateDerivedData(new_text, this.denson_status, this.denson_year);
  }

  saveOnFirstPaste(pasted_text) {
    // If user is pasting a substantial amount of text into an empty field,
    // then save a version (if needed - but not if pasted text already matches top of history).
    if (!(typeof pasted_text === 'string')) {
      return;
    }
    if (!(this.state.source_text.length === 0)) {
      return;
    }
    if (!(pasted_text.length > 10)) {
      return;
    }
    this.saveAVersionIfNeeded(pasted_text);
  }

  // Helper functions used by Render to help component UI State reflect Document State

  ableToResetDocumentData() {
    // The only user-meaningful state is in the source_text and the prior_versions.
    if (
      typeof this.state.source_text === 'string' &&
      this.state.source_text.length > 0
    ) {
      return true;
    }
    if (this.state.prior_versions.length > 0) {
      return true;
    }
    return false;
  }

  textMatchesVersion(i) {
    return this.state.source_text === this.state.prior_versions[i];
  }

  versionButtonStyle(i) {
    return !this.state.prior_activated
      ? 'info'
      : i === this.state.prior_number && this.textMatchesVersion(i)
        ? 'success'
        : i === this.state.prior_number
          ? 'info'
          : i < this.state.prior_number ? 'info' : 'default';
  }

  // reCalculateDerivedData
  //
  // Rebuild data derived entirely from these two possibly-changed values
  // of Document State variables: new_text (which can be empty) and
  // new_denson_status (which can be null).
  // Must be "pure" -- no side effects except to store calculation
  // results via this.SetState.  No other calls to "this." functions.
  // No side effects.
  //
  // Should only be called as the last step of handling a user event, not
  // from other "internal" calls.

  reCalculateDerivedData(
    new_text,
    new_denson_status = null,
    new_denson_year = null
  ) {
    // Mini-clean: Split into the 3 header lines, plus the body.  Tolerate extra newlines around header rows if present.

    let parts =
      new_text.match(/^\n*([^\n]+)\n+([^\n]+)\n+([^\n]+)\n+([^]+?)[-\s]*$/) ||
      [];
    let name_line = parts[1] || '';
    let name = newline_escape_remove(name_line); // ignore element 0 (whole match)
    let location_line = parts[2] || '';
    let location = newline_escape_remove(location_line);
    let date_line = parts[3] || '';
    let body = parts[4] || '';
    let year = (date_line.match(/\d{4}/) || [])[0] || '';

    // Determine whether cleaning seems to be needed.

    let needs_cleaning = clean_minutes_markup(new_text) !== new_text;
    this.setState((previousState, currentProps) =>
      update(previousState, {
        needs_cleaning: { $set: needs_cleaning }
      })
    );

    // Try to guess denson status.

    let guessed_denson_status =
      name && location && year && body
        ? guess_denson(name, location, year, body)
        : null;

    // Set the guessed value, and also set the actual value if user hasn't selected one yet
    this.setState((previousState, currentProps) => {
      let updates = {
        guessed_denson_status: { $set: guessed_denson_status }
      };
      // If no user selection exists and we don't have an explicit new_denson_status, set it to the guess
      if (
        !new_denson_status &&
        previousState.denson_status === null &&
        guessed_denson_status !== null
      ) {
        updates.denson_status = { $set: guessed_denson_status };
      }
      return update(previousState, updates);
    });

    // Try to find song references and get marked-up source text showing them.
    // save the results.

    // We will presume what the user told us re: denson_status; otherwise we will go with our guess.

    let presumed_denson_status =
      new_denson_status || this.state.denson_status || guessed_denson_status;

    // Try to guess denson year based on the presumed denson status.
    let guessed_denson_year =
      name && year
        ? guess_denson_year(name, year, presumed_denson_status)
        : null;

    // Set the guessed value, and also set the actual value if user hasn't selected one yet
    this.setState((previousState, currentProps) => {
      let updates = {
        guessed_denson_year: { $set: guessed_denson_year }
      };
      // If no user selection exists, set it to the guess
      if (previousState.denson_year === null && guessed_denson_year !== null) {
        updates.denson_year = { $set: guessed_denson_year };
      }
      return update(previousState, updates);
    });
    let treat_as_denson =
      presumed_denson_status === 'denson' || presumed_denson_status === 'mixed';

    let presumed_denson_year =
      new_denson_year || this.state.denson_year || guessed_denson_year;

    // console.log('presumed_denson_status', presumed_denson_status);

    let refs = treat_as_denson
      ? analyze_denson_song_references(body, presumed_denson_year)
      : {};

    // if (source_text_with_song_markups) console.log(source_text_with_song_markups);

    let source_text_with_song_markups =
      name_line && location_line && date_line && refs.body_with_markups
        ? `${name_line}\n${location_line}\n${date_line}\n\n${refs.body_with_markups}`
        : null;

    let [good, corrected, ambiguous, bad, problems, total] = [];
    this.setState((previousState, currentProps) =>
      update(previousState, {
        source_text_with_song_markups: { $set: source_text_with_song_markups },
        song_markups_known_good: { $set: refs.song_markups_known_good || [] },
        song_markups_corrected: { $set: refs.song_markups_corrected || [] },
        song_markups_ambiguous: { $set: refs.song_markups_ambiguous || [] },
        song_markups_bad: { $set: refs.song_markups_bad || [] },
        song_markups_known_good_count: {
          $set: (good = (refs.song_markups_known_good || []).length)
        },
        song_markups_corrected_count: {
          $set: (corrected = (refs.song_markups_corrected || []).length)
        },
        song_markups_ambiguous_count: {
          $set: (ambiguous = (refs.song_markups_ambiguous || []).length)
        },
        song_markups_bad_count: {
          $set: (bad = (refs.song_markups_bad || []).length)
        },

        song_markups_problem_count: {
          $set: (problems = corrected + ambiguous + bad)
        },
        song_markups_total_count: { $set: (total = good + problems) },
        song_markups_problem_percent: {
          $set: good && total ? Math.ceil(problems * 100 / total) : 0
        }
      })
    );

    // Render either the original text or the marked up text.

    let res = render_minutes_markup(
      source_text_with_song_markups || new_text,
      treat_as_denson,
      presumed_denson_year
    );

    this.setState((previousState, currentProps) =>
      update(previousState, {
        rendered_html: { $set: res.rendered_html || empty_preview },
        name_line: { $set: name },
        location_line: { $set: location },
        date_line: { $set: date_line },
        start_date: { $set: res.start_date },
        end_date: { $set: res.end_date },
        day_count: { $set: res.day_count },
        date_range_display: {
          $set: dateRangeAsString(res.start_date, res.end_date)
        }
      })
    );
  }

  render() {
    return (
      <Grid className="MinutesEditor" fluid={true}>
        <TestCaseButtonToolbar
          shown={this.state.test_cases_expanded}
          test_cases={test_cases}
          clickHandler={i => this.setTestCaseEvent(i)}
        />
        <Row className="show-grid">
          <Col md={4} style={{ minWidth: '5.5in' }}>
            <h3 style={{ width: '100%' }}>
              <small>
                <Glyphicon glyph="pencil" />
              </small>{' '}
              Edit
              <CleanButton
                onClick={this.cleanMeEvent}
                disabled={!this.state.needs_cleaning}
                tooltipText="Apply standard formatting."
              />
            </h3>
            <div style={{ marginBottom: '0.5em' }}>
              <strong style={{ marginRight: '0.5em' }}>Denson Status:</strong>
              <DensonRadioControls
                current_hint={this.state.guessed_denson_status}
                current_value={this.state.denson_status}
                disabled={
                  !(this.state.source_text && this.state.source_text.length)
                }
                onChange={e => this.setDensonStatusEvent(e.target.value)}
              />
            </div>
            <div style={{ marginBottom: '0.5em' }}>
              <strong style={{ marginRight: '0.5em' }}>Denson Year:</strong>
              <DensonYearRadioControls
                current_hint={this.state.guessed_denson_year}
                current_value={this.state.denson_year}
                disabled={
                  !(this.state.source_text && this.state.source_text.length)
                }
                onChange={e => this.setDensonYearEvent(e.target.value)}
              />
            </div>
            <SourceEditorInput
              placeholder="Paste minutes here..."
              className="minutes_input"
              onChange={e => this.setSourceTextEvent(e.target.value)}
              onPaste={e =>
                this.saveOnFirstPaste(e.clipboardData.getData('Text'))}
              value={this.state.source_text}
            />
            <DocVersionToolbar
              trashFunction={() => this.resetDocumentDataEvent()}
              trashDisabled={!this.ableToResetDocumentData()}
              trashTooltip="Clear editing area, and delete all saved versions."
              trashConfirmMessage="Really delete edited text and all saved versions?"
              historyToolTipTextGenerator={i =>
                this.versionButtonStyle(i) === 'success'
                  ? `You are viewing saved copy number ${i + 1}.`
                  : `Go to saved copy number ${i + 1}.`}
              historyButtonStyler={i => this.versionButtonStyle(i)}
              historyItemCount={this.state.prior_versions.length}
              historyClickHandler={i => this.revertToVersionEvent(i)}
              historyButtonLabeler={i =>
                this.versionButtonStyle(i) === 'success' ? (
                  <FaCheck />
                ) : (
                  <FaRotateLeft />
                )}
              saveButtonActive={this.needToSave(this.state.source_text)}
              saveButtonTooltip="Save a copy reflecting current changes."
              saveButtonClickHandler={e =>
                this.saveAVersionIfNeeded(this.state.source_text)}
            />
          </Col>
          <Col md={4} style={{ minWidth: '5.5in' }}>
            <PreviewDisplay rendered_html={this.state.rendered_html} />
          </Col>
          <Col md={4} style={{ minWidth: '5.5in' }}>
            <InfoDisplay
              name_line={this.state.name_line}
              location_line={this.state.location_line}
              date_range_display={this.state.date_range_display}
              day_count={this.state.day_count}
              guessed_denson_status={this.state.guessed_denson_status}
              denson_status={this.state.denson_status}
              guessed_denson_year={this.state.guessed_denson_year}
              denson_year={this.state.denson_year}
              song_markups_known_good={this.state.song_markups_known_good}
              song_markups_corrected={this.state.song_markups_corrected}
              song_markups_ambiguous={this.state.song_markups_ambiguous}
              song_markups_bad={this.state.song_markups_bad}
              song_markups_problem_count={this.state.song_markups_problem_count}
              song_markups_problem_percent={
                this.state.song_markups_problem_percent
              }
            />
          </Col>
        </Row>
      </Grid>
    );
  }
}

function renderPercentage(conditional, number, threshhold) {
  let color = number < threshhold ? 'red' : 'inherit';
  return conditional ? <span style={{ color: color }}>{number}%</span> : null;
}

function toTitleCase(string) {
  return (
    string &&
    string.replace(/[a-zA-Z']+/g, word => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
  );
}

function dateRangeAsString(start_date, end_date) {
  let date_string = '';
  let format = 'dddd, mmmm d, yyyy';
  if (
    Object.prototype.toString.call(start_date) !== '[object Date]' ||
    Object.prototype.toString.call(end_date) !== '[object Date]'
  ) {
    date_string = '';
  } else if (start_date.getTime() === end_date.getTime()) {
    date_string = dateFormat(start_date, format);
  } else {
    date_string = `${dateFormat(start_date, format)} - ${dateFormat(
      end_date,
      format
    )}`;
  }
  return date_string;
}

function InnerHTML({ html }) {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

const DensonRadioControls = ({
  current_hint,
  current_value,
  disabled,
  onChange
}) => (
  <FormGroup style={{ display: 'inline', marginLeft: '0' }}>
    <small>
      <HintedRadioButtons
        value_list={['denson', 'mixed', 'non-denson']}
        current_hint={current_hint}
        current_value={current_value}
        disabled={disabled}
        onChange={onChange}
        valueListToLabel={val => toTitleCase(val)}
      />
    </small>
  </FormGroup>
);

const DensonYearRadioControls = ({
  current_hint,
  current_value,
  disabled,
  onChange
}) => (
  <FormGroup style={{ display: 'inline', marginLeft: '0' }}>
    <small>
      <HintedRadioButtons
        value_list={['2025', '1991', 'N/A']}
        current_hint={current_hint}
        current_value={current_value}
        disabled={disabled}
        onChange={onChange}
        valueListToLabel={val => val}
      />
    </small>
  </FormGroup>
);

const SourceEditorInput = ({
  placeholder,
  className,
  onChange,
  onPaste,
  value
}) => (
  <DebounceInput
    element="textarea"
    forceNotifyByEnter={false}
    placeholder={placeholder}
    className="minutes_input"
    onChange={onChange}
    onPaste={onPaste}
    value={value}
    debounceTimeout={200}
  />
);

const PreviewDisplay = ({ rendered_html }) => (
  <div>
    <h3>
      <small>
        <Glyphicon glyph="eye-open" />
      </small>{' '}
      Preview
    </h3>
    <div>
      <InnerHTML html={rendered_html} />
    </div>
  </div>
);

const InfoDisplay = ({
  name_line,
  location_line,
  date_range_display,
  day_count,
  guessed_denson_status,
  denson_status,
  guessed_denson_year,
  denson_year,
  song_markups_known_good,
  song_markups_corrected,
  song_markups_ambiguous,
  song_markups_bad,
  song_markups_problem_count,
  song_markups_problem_percent
}) => (
  <div>
    <h3>
      <small>
        <Glyphicon glyph="info-sign" />
      </small>{' '}
      Info
    </h3>
    <Panel collapsible expanded={true} style={{ width: '5.25in' }}>
      <dl className="dl-horizontal">
        <dt>Name</dt>
        <dd>{name_line}</dd>
        <dt>Location</dt>
        <dd>{location_line}</dd>
        <dt>Dates</dt>
        <dd>{date_range_display}</dd>
        <dt>Days</dt>
        <dd>{day_count || ''}</dd>
        <dt>Denson Status Guess</dt>
        <dd>{toTitleCase(guessed_denson_status)}</dd>
        <dt>Selected Denson Status</dt>
        <dd>{toTitleCase(denson_status)}</dd>
        <dt>Denson Year Guess</dt>
        <dd>{toTitleCase(guessed_denson_year)}</dd>
        <dt>Selected Denson Year</dt>
        <dd>{toTitleCase(denson_year)}</dd>
        <dt>Recognition Score</dt>
        <dd>
          {renderPercentage(
            song_markups_known_good.length,
            100 - song_markups_problem_percent,
            90
          )}
        </dd>
        <dt>
          {song_markups_problem_count || ''} Song{song_markups_problem_count !== 1 ? 's ' : ' '}{' '}
          With Issues
        </dt>
        <dd>
          {song_markups_known_good.length
            ? [
                song_markups_corrected.length
                  ? 'Correctable: ' + song_markups_corrected.join(', ')
                  : null,
                song_markups_ambiguous.length
                  ? 'Ambiguous: ' + song_markups_ambiguous.join(', ')
                  : null,
                song_markups_bad.length
                  ? 'Unrecognized: ' + song_markups_bad.join(', ')
                  : null
              ]
                .filter(i => i)
                .join('; ') || '—'
            : ''}
        </dd>
        <dt>
          {song_markups_known_good.length || ''} Song{song_markups_known_good.length !== 1 ? 's ' : ' '}
          Recognized
        </dt>
        <dd>{song_markups_known_good.join(', ')}</dd>
      </dl>
    </Panel>
  </div>
);

const HintedRadioButtons = ({
  value_list,
  current_hint,
  current_value,
  disabled,
  onChange,
  valueListToLabel
}) => {
  return [...Array(value_list.length).keys()].map(i => (
    <Radio
      key={i}
      value={value_list[i]}
      inline
      style={
        current_hint === value_list[i] ? { textDecoration: 'underline' } : {}
      }
      disabled={disabled}
      checked={current_value === value_list[i]}
      onChange={onChange}
    >
      {valueListToLabel(value_list[i])}
    </Radio>
  ));
};

// A hideable row of test case injection buttons.
const TestCaseButtonToolbar = ({ shown, test_cases, clickHandler }) => (
  <ButtonToolbar style={{ display: shown ? 'block' : 'none' }}>
    <ButtonGroup>
      <ButtonSequence
        item_count={test_cases.length}
        clickHandler={clickHandler}
        buttonLabeler={i => i + 1}
        toolTipTextGenerator={i => `Apply test case ${i + 1}`}
      />
    </ButtonGroup>
  </ButtonToolbar>
);

const DocVersionToolbar = ({
  trashFunction,
  trashDisabled,
  trashTooltip,
  trashConfirmMessage,
  historyToolTipTextGenerator,
  historyButtonStyler,
  historyItemCount,
  historyClickHandler,
  historyButtonLabeler,
  saveButtonActive,
  saveButtonTooltip,
  saveButtonClickHandler
}) => (
  <ButtonToolbar>
    <TrashButton
      trashFunction={trashFunction}
      disabled={trashDisabled}
      tooltip={trashTooltip}
      confirmMessage={trashConfirmMessage}
    />
    <HistoryButtons
      toolTipTextGenerator={historyToolTipTextGenerator}
      buttonStyler={historyButtonStyler}
      item_count={historyItemCount}
      clickHandler={historyClickHandler}
      buttonLabeler={historyButtonLabeler}
      saveButton={
        <SaveButton
          shown={saveButtonActive}
          tooltip={saveButtonTooltip}
          onClick={saveButtonClickHandler}
          disabled={!saveButtonActive}
        />
      }
    />
  </ButtonToolbar>
);

const CleanButton = ({ onClick, disabled, tooltipText = '' }) => (
  <Button
    style={{ fontSize: '1.5rem' }}
    bsStyle="warning"
    data-tooltip={tooltipText}
    onClick={onClick}
    disabled={disabled}
  >
    <span>
      <FaDiamond /> <FaDiamond /> <FaDiamond />
    </span>
  </Button>
);

const TrashButton = ({ trashFunction, disabled, tooltip, confirmMessage }) => (
  <ButtonGroup>
    <Button
      data-tooltip={tooltip}
      bsStyle="info"
      onClick={e => {
        if (window.confirm(confirmMessage)) {
          trashFunction();
        }
      }}
      disabled={disabled}
    >
      <FaTrash />
    </Button>
  </ButtonGroup>
);

const HistoryButtons = ({
  toolTipTextGenerator,
  buttonStyler,
  item_count,
  clickHandler,
  buttonLabeler,
  saveButton
}) => (
  <ButtonGroup>
    <ButtonSequence
      toolTipTextGenerator={toolTipTextGenerator}
      buttonStyler={buttonStyler}
      item_count={item_count}
      clickHandler={clickHandler}
      buttonLabeler={buttonLabeler}
    />{' '}
    {saveButton}
  </ButtonGroup>
);

const SaveButton = ({ shown, tooltip, onClick, disabled }) =>
  shown ? (
    <Button
      data-tooltip={tooltip}
      bsStyle="info"
      onClick={onClick}
      disabled={disabled}
    >
      <FaPlusSquare />
    </Button>
  ) : (
    ''
  );

export default MinutesEditor;



// WEBPACK FOOTER //
// ./src/MinutesEditor.js